import { useRef, useState } from "react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { ResultBlock } from "@/components/GuestImportDialog";

const CSV_TEMPLATE = `title,start_time,end_time,location,description,tag,max_signups,color,is_public
Friday Run Club,2026-04-10T07:00:00Z,2026-04-10T08:00:00Z,Riverside,Easy 5k,sports,0,#DCE4D8,false
All Hands Q2,2026-04-15T15:00:00Z,2026-04-15T16:00:00Z,Main Hall,Quarterly update,company,40,#D8DEE5,false
`;

export default function EventImportDialog({ open, onOpenChange, onImported }) {
    const fileRef = useRef();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => { setResult(null); setBusy(false); };
    const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

    const downloadTemplate = () => {
        const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "events-template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/activities/import`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            onImported?.();
            toast.success(`${data.added} event${data.added === 1 ? "" : "s"} imported${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg rounded-none border-[#E5E5E3] p-0 shadow-none" data-testid="event-import-dialog">
                <div className="p-8">
                    <DialogHeader className="text-left space-y-1">
                        <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">Import</p>
                        <DialogTitle className="font-heading text-2xl font-light tracking-tight text-[#111110]">Bulk-add events from CSV or .ics</DialogTitle>
                        <DialogDescription className="text-sm text-[#666664]">
                            Upload a CSV (use the template) or any standard <code className="text-[#111110]">.ics</code> file (Google / Apple / Outlook export). Duplicates are skipped by title + start time.
                        </DialogDescription>
                    </DialogHeader>

                    {!result ? (
                        <div className="mt-6 space-y-4">
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                disabled={busy}
                                data-testid="event-file-dropzone"
                                className="w-full border border-dashed border-[#E5E5E3] hover:border-[#888885] transition-colors p-10 flex flex-col items-center justify-center gap-2 text-center"
                            >
                                <FileText className="w-6 h-6 text-[#666664]" strokeWidth={1.5} />
                                <span className="text-sm text-[#111110] font-medium">{busy ? "Importing…" : "Choose .csv or .ics file"}</span>
                                <span className="text-xs text-[#888885]">CSV columns: title, start_time, end_time, location?, description?, tag?, max_signups?, color?, is_public?</span>
                            </button>
                            <input ref={fileRef} type="file" accept=".csv,.ics,text/csv,text/calendar" className="hidden"
                                onChange={(e) => upload(e.target.files?.[0])} data-testid="event-file-input" />
                            <button
                                type="button"
                                onClick={downloadTemplate}
                                data-testid="event-csv-template-btn"
                                className="text-xs tracking-[0.15em] uppercase text-[#666664] hover:text-[#111110] inline-flex items-center gap-1.5"
                            >
                                <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Download CSV template
                            </button>
                        </div>
                    ) : (
                        <ResultBlock result={result} testId="event-import-result" labels={{ added: "Added", skipped: "Skipped", total: "Parsed" }} />
                    )}
                </div>

                <DialogFooter className="border-t border-[#E5E5E3] px-8 py-5 bg-[#F9F9F8] flex-row gap-2 justify-end">
                    <Button variant="outline" onClick={() => handleClose(false)}
                        className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE]"
                        data-testid="event-import-close-btn">
                        {result ? "Done" : "Cancel"}
                    </Button>
                    {!result && (
                        <Button onClick={() => fileRef.current?.click()} disabled={busy}
                            className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-6"
                            data-testid="event-import-pick-btn">
                            <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} /> Choose file
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
